import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { OtpScreenKey } from 'features/otp/constants/OtpScreenKey';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import { useCallback } from 'react';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';

export interface AnalyticsErrorState {
  reason: string;
  code: number | string;
  message: string;
  title: string;
}

interface UseOtpAnalyticsProps {
  flowKey: OtpFlowKey;
  verificationType: OtpVerificationType;
  otpSubmitAttempt: number;
  isSuccess: boolean;
  analyticsError: AnalyticsErrorState | null;
}

export const useOtpAnalytics = ({
  flowKey,
  verificationType,
  otpSubmitAttempt,
  isSuccess,
  analyticsError,
}: UseOtpAnalyticsProps) => {
  let screenKey = '';
  let customAttributes: Record<string, string> | null = null;

  if (
    verificationType === OtpVerificationType.EMAIL &&
    flowKey === OtpFlowKey.LOGIN
  ) {
    screenKey = OtpScreenKey.LOGIN_VERIFY_EMAIL;
  } else if (verificationType === OtpVerificationType.EMAIL) {
    screenKey = OtpScreenKey.SIGNUP_VERIFY_EMAIL;
  } else if (flowKey === OtpFlowKey.LOGIN) {
    screenKey = OtpScreenKey.LOGIN_VERIFY_SMS;
  } else if (flowKey === OtpFlowKey.REACTIVATION) {
    screenKey = OtpScreenKey.VERIFY_SMS_REACTIVATION;
    customAttributes = {
      reactivation_flow: 'true',
    };
  } else if (flowKey === OtpFlowKey.PROFILE_CHANGE) {
    screenKey = OtpScreenKey.PHONE_CHANGE_OTP;
  } else if (flowKey === OtpFlowKey.CARD_CREDENTIALS) {
    screenKey = OtpScreenKey.CARD_CREDENTIALS_OTP;
  } else {
    screenKey = OtpScreenKey.SIGNUP_VERIFY_SMS;
  }

  // Screen focus analytics
  useViewDidFocusScreenAnalyticsEvent({
    key: screenKey,
    customAttributes: customAttributes,
  });

  const stepName =
    verificationType === OtpVerificationType.EMAIL
      ? 'email_verification'
      : 'phone_number_verification';

  useStepStartedFocusAnalytics(
    flowKey !== OtpFlowKey.LOGIN
      ? {
          analyticsId: 'verifyStepStarted',
          customAttributes: {
            step_name: stepName,
          },
        }
      : undefined,
  );

  // Step completed analytics
  useStepStartedFocusAnalytics(
    isSuccess
      ? {
          analyticsId:
            verificationType === OtpVerificationType.EMAIL
              ? 'emailVerifyCompleted'
              : 'otpVerifyCompleted',
          customAttributes: {
            step_name: stepName,
          },
        }
      : undefined,
  );

  // Verification code submitted analytics
  const handleVerificationCodeSubmitted = usePressableAnalytics({
    analyticsId: 'verificationCodeSubmitted',
    customAttributes: {
      verification_attempt: otpSubmitAttempt,
      verification_type:
        verificationType === OtpVerificationType.EMAIL ? 'email' : 'phone',
    },
  });

  // Entry method analytics - different for email vs phone
  const handleManualEntryAnalytics = usePressableAnalytics({
    analyticsId:
      verificationType === OtpVerificationType.EMAIL
        ? 'emailManuallyEntered'
        : 'smsManuallyEntered',
    customAttributes: {
      verification_attempt: otpSubmitAttempt,
      verification_type: verificationType,
    },
  });

  const trackVerificationFailed = usePressableAnalytics({
    analyticsId: 'otp-failed-validation',
    customAttributes: {
      verification_attempt: otpSubmitAttempt,
      verification_type: verificationType,
      error: analyticsError,
    },
  });

  // Handler for tracking verification attempt
  const trackVerificationAttempt = useCallback(() => {
    if (flowKey === OtpFlowKey.LOGIN || flowKey === OtpFlowKey.PROFILE_CHANGE) {
      handleVerificationCodeSubmitted();
    } else {
      handleManualEntryAnalytics();
    }
  }, [flowKey, handleVerificationCodeSubmitted, handleManualEntryAnalytics]);

  return {
    trackVerificationAttempt,
    trackVerificationFailed,
  };
};
